import React from 'react';
import { cn } from '@/lib/utils';
import { COUNTER_WARN_RATIO } from '@/constants/contentLimits';

interface CharCounterProps {
  value: string;
  max: number;
  /** Zusatzhinweis links neben dem Zähler, z. B. warum kurz besser ist. */
  hint?: string;
  className?: string;
}

/**
 * Dezenter „123 / 700"-Zähler unter einem Textfeld.
 *
 * Bleibt unauffällig, solange reichlich Platz ist, und meldet sich erst, wenn es
 * eng wird — ein Zähler, der von Anfang an rot leuchtet, wird ignoriert. Das
 * Feld selbst begrenzt über `maxLength` hart; der Zähler erklärt nur, warum
 * plötzlich nichts mehr ankommt.
 */
export function CharCounter({ value, max, hint, className }: CharCounterProps) {
  const used = value.length;
  const full = used >= max;
  const near = !full && used >= max * COUNTER_WARN_RATIO;

  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-3 text-xs',
        className,
      )}
    >
      {hint ? (
        <span className="text-muted-foreground/80 leading-snug">{hint}</span>
      ) : (
        <span />
      )}
      <span
        aria-live={near || full ? 'polite' : 'off'}
        className={cn(
          'shrink-0 tabular-nums',
          full
            ? 'font-medium text-destructive-strong'
            : near
              ? 'font-medium text-amber-600 dark:text-amber-500'
              : 'text-muted-foreground/70',
        )}
      >
        {used} / {max}
        {full && <span className="ml-1">· Maximum erreicht</span>}
      </span>
    </div>
  );
}

export default CharCounter;
